import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDate } from "@/lib/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Trash2 } from "lucide-react";
import { useState } from "react";
import { MediaPreviewDialog } from "@/components/history/MediaPreviewDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface DocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId: string;
  supplierName: string;
}

interface Document {
  id: string;
  content: string;
  document_url: string;
  created_at: string;
}

export const DocumentsDialog = ({
  open,
  onOpenChange,
  supplierId,
  supplierName,
}: DocumentsDialogProps) => {
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: documents, isLoading } = useQuery({
    queryKey: ["supplier-documents", supplierId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_communications")
        .select("*")
        .eq("supplier_id", supplierId)
        .eq("type", "document")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Document[];
    },
  });

  const handleDelete = async (docId: string, documentUrl: string) => {
    try {
      // Extract the file path from the URL
      const filePathMatch = documentUrl.match(/supplier_documents\/(.+)$/);
      if (!filePathMatch) {
        throw new Error("Invalid document URL");
      }
      const filePath = filePathMatch[1];

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("supplier_documents")
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("supplier_communications")
        .delete()
        .eq("id", docId);

      if (dbError) throw dbError;

      // Refresh the documents list
      queryClient.invalidateQueries({ queryKey: ["supplier-documents", supplierId] });
      toast.success("Document supprimé avec succès");
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Erreur lors de la suppression du document");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Documents - {supplierName}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            {isLoading ? (
              <div className="text-center py-4">Chargement...</div>
            ) : documents?.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                Aucun document enregistré
              </div>
            ) : (
              <div className="space-y-4">
                {documents?.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-4 border rounded-lg space-y-2 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer"
                        onClick={() => setSelectedDocument(doc.document_url)}
                      >
                        <FileText className="h-4 w-4" />
                        <span>{formatDate(doc.created_at)}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(doc.id, doc.document_url)}
                        className="text-destructive hover:text-destructive/90"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-sm">{doc.content}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <MediaPreviewDialog
        open={!!selectedDocument}
        onOpenChange={(open) => !open && setSelectedDocument(null)}
        url={selectedDocument || ""}
      />
    </>
  );
};